import {
    createContext,
    type ReactNode,
    useContext,
    useEffect,
    useState,
    useSyncExternalStore,
} from 'react';
import { cleanupFormTracking, initializeAnalytics, waitForAnalyticsReady } from '../analytics';
import { resolvePageLoadEventPolicy } from '../analytics/pageLoadEventPolicy';
import { FRONTEND_VERSION, getGitCommitSha } from '../config/constants';
import { isCurrentPageCTADestination, shouldEnableFormTracking } from '../config/SiteConfigManager';
import { cleanupPostRequestCapture, setupPostRequestCapture } from '../postCapture/manager';
import { getConsentMode, subscribeConsentMode } from '../services/consentMode';
import { logger } from '../utils/core/logger';
import { getFullDomain, getRootDomain } from '../utils/domain/domain';
import { isHostInAllowlistScope, isUrlAllowed } from '../utils/domain/siteRestrictions';
import {
    cleanupCtaLinkAttribution,
    setupCtaLinkAttribution,
} from '../utils/web/ctaLinkAttribution';
import { useSiteConfig } from './SiteConfigContext';

interface AnalyticsContextValue {
    isAnalyticsReady: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(undefined);

interface AnalyticsProviderProps {
    children: ReactNode;
}

/**
 * AnalyticsProvider - Handles analytics initialization after site config is loaded
 *
 * This provider must be nested inside ConfigProvider to access site configuration.
 * It initializes the analytics system (PostHog, form tracking, etc.) only after
 * site config is available.
 *
 * Execution order:
 * 1. ConfigProvider loads config from unified config tables (step 1️⃣)
 * 2. ConfigProvider creates service and fetches API version (step 2️⃣)
 * 3. AnalyticsProvider initializes analytics (step 3️⃣)
 * 4. Child components can render safely (step 4️⃣)
 */
export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
    const siteConfigContext = useSiteConfig();
    const {
        currentDomain,
        currentResolver,
        isLoaded,
        error,
        deploymentTarget,
        apiVersion,
        environment,
        inboundXService,
    } = siteConfigContext;
    const [isAnalyticsReady, setIsAnalyticsReady] = useState(false);
    const consentMode = useSyncExternalStore(subscribeConsentMode, getConsentMode, () => 'pending');

    useEffect(() => {
        if (
            consentMode !== 'full' ||
            error ||
            !isLoaded ||
            !currentResolver ||
            !currentDomain ||
            !inboundXService
        ) {
            cleanupPostRequestCapture();
            return;
        }

        const postRequestCapture = currentResolver.analytics.post_request_capture;
        setupPostRequestCapture({
            enabled: postRequestCapture?.enabled === true,
            redactPayloadValues: postRequestCapture?.redact_payload_values !== false,
            pagePatterns: postRequestCapture?.page_patterns ?? [],
            siteDomain: currentDomain,
            transport: inboundXService,
        });
    }, [consentMode, currentDomain, currentResolver, error, inboundXService, isLoaded]);

    useEffect(() => {
        if (consentMode === 'full' && isAnalyticsReady && currentDomain) {
            setupCtaLinkAttribution(currentDomain);
        }
        return cleanupCtaLinkAttribution;
    }, [consentMode, isAnalyticsReady, currentDomain]);

    useEffect(() => {
        return () => {
            cleanupPostRequestCapture();
        };
    }, []);

    useEffect(() => {
        // Don't initialize analytics if there was an error during config loading
        if (error) {
            logger.debug('🌍 3️⃣ ⏸️ Skipping analytics initialization due to config error:', error);
            return;
        }

        // Wait for site config to load before initializing analytics
        if (!isLoaded || !currentResolver || !currentDomain) {
            logger.debug('🌍 3️⃣ ⏸️ Waiting for unified config before analytics initialization');
            return;
        }

        // Wait for InboundXService to be available (it has the sessionId)
        if (!inboundXService) {
            logger.debug('🌍 3️⃣ ⏸️ Waiting for InboundXService before analytics initialization');
            return;
        }

        // Get sessionId from the service - this ensures sessionId is in analytics context
        // BEFORE isAnalyticsReady becomes true, preventing race condition with widget_impression
        const sessionId = inboundXService.getSessionId();
        logger.debug('🌍 3️⃣ Got sessionId from InboundXService:', sessionId);

        // Get domains from window.location.hostname - single source of truth
        // These will throw if not in browser environment - app should crash, not silently fail
        const fullDomain = getFullDomain();
        const rootDomain = getRootDomain();

        logger.info('🌍 3️⃣ Initializing analytics with loaded config', {
            fullDomain,
            rootDomain,
            deploymentTarget,
            hasResolver: true,
        });

        // CRITICAL: Validate apiVersion and environment are available BEFORE initializing analytics.
        if (!apiVersion || !environment) {
            logger.error(
                '❌ CRITICAL: API version/environment missing - this indicates a bug in ConfigProvider'
            );
            throw new Error(
                'API version and environment are required but not available from ConfigProvider'
            );
        }

        let cancelled = false;

        // Initialize analytics with apiVersion and environment in globalContext so
        // providers have full context before any events are emitted.
        const initializeAnalyticsAsync = async () => {
            try {
                // Decide which page-load events posthog-js may emit, BEFORE it initializes —
                // capture_pageview/capture_pageleave are init-time options, and posthog fires
                // its single $pageview synchronously during init. Every input here is derived
                // from config already in memory, so this adds no latency and no dependency on
                // PostHog itself (unlike traffic control, which needs a distinct_id).
                const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
                const urlTargeting = {
                    display_only_on_urls: currentResolver.traffic_control.display_only_on_urls,
                    custom_config: {
                        page_display_rules: currentResolver.traffic_control.page_display_rules,
                    },
                };
                const pageLoadPolicy = resolvePageLoadEventPolicy({
                    urlEligible: isUrlAllowed(urlTargeting, pageUrl),
                    hostInScope: isHostInAllowlistScope(urlTargeting, pageUrl),
                    // A page Rose watches without displaying (CTA destination, form-tracking
                    // page, thank-you page) keeps its $pageview so the conversion it captures
                    // still has a visitor_sessions row to attach to.
                    isTrackedSurface:
                        shouldEnableFormTracking(currentDomain) ||
                        isCurrentPageCTADestination(currentDomain, pageUrl),
                    mode: currentResolver.analytics.page_load_event_suppression ?? 'off',
                });
                logger.debug('📊 Page-load event policy resolved', pageLoadPolicy);

                await initializeAnalytics({
                    posthog: {
                        capturePageview: pageLoadPolicy.capturePageview,
                        capturePageleave: pageLoadPolicy.capturePageleave,
                    },
                    globalContext: {
                        domain: fullDomain,
                        rootDomain: rootDomain,
                        deploymentTarget,
                        frontendVersion: FRONTEND_VERSION,
                        gitCommitSha: getGitCommitSha(),
                        // CRITICAL: Include apiVersion and environment in initial context
                        // This prevents race condition - environment is set before PostHog is ready
                        apiVersion,
                        environment,
                        sessionId,
                    },
                    debug:
                        (typeof import.meta !== 'undefined' &&
                            (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV) ||
                        false,
                    enableFormTracking: true,
                });

                logger.debug(
                    '✅ Analytics initialized with apiVersion and environment from SiteConfig:',
                    {
                        apiVersion,
                        environment,
                        note: 'Environment is now set synchronously in globalContext, preventing race conditions',
                    }
                );

                // STEP 3: Ensure PostHog provider is registered and ready before rendering widget
                await waitForAnalyticsReady();
                if (cancelled) return;
                logger.info('🌍 3️⃣ ✅ Analytics initialized and PostHog ready');
                setIsAnalyticsReady(true);
            } catch (error) {
                logger.error('❌ Analytics initialization failed:', error);
                if (!cancelled) setIsAnalyticsReady(true);
            }
        };

        // Run async initialization
        initializeAnalyticsAsync();

        // Cleanup on unmount
        return () => {
            cancelled = true;
            cleanupFormTracking();
            cleanupCtaLinkAttribution();
            setIsAnalyticsReady(false);
        };
    }, [
        isLoaded,
        currentDomain,
        currentResolver,
        apiVersion,
        environment,
        error,
        inboundXService,
        deploymentTarget,
    ]);

    const contextValue: AnalyticsContextValue = {
        isAnalyticsReady,
    };

    return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = (): AnalyticsContextValue => {
    const context = useContext(AnalyticsContext);
    if (!context) {
        throw new Error('useAnalytics must be used within an AnalyticsProvider');
    }
    return context;
};
